import Select, {
    Props as SelectProps,
    StylesConfig,
    GroupBase,
} from 'react-select';

type StyleKey =
    | 'control'
    | 'menu'
    | 'option'
    | 'input'
    | 'singleValue'
    | 'multiValue'
    | 'multiValueLabel'
    | 'multiValueRemove'
    | 'valueContainer'
    | 'placeholder'
    | 'indicatorSeparator'
    | 'dropdownIndicator'
    | 'clearIndicator'
    | 'menuPortal';

const getDefaultMenuPortalTarget = () =>
    typeof document === 'undefined' ? undefined : document.body;

function mergeStyles<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
>(
    base: StylesConfig<Option, IsMulti, Group>,
    override?: StylesConfig<Option, IsMulti, Group>
): StylesConfig<Option, IsMulti, Group> {
    const merged = {...base};
    const keys: StyleKey[] = [
        'control',
        'menu',
        'option',
        'input',
        'singleValue',
        'multiValue',
        'multiValueLabel',
        'multiValueRemove',
        'valueContainer',
        'placeholder',
        'indicatorSeparator',
        'dropdownIndicator',
        'clearIndicator',
        'menuPortal',
    ];

    for (const key of keys) {
        const baseFn = base[key];
        const overrideFn = override?.[key];
        if (overrideFn) {
            merged[key] = (provided: any, state: any) =>
                overrideFn(
                    typeof baseFn === 'function'
                        ? baseFn(provided, state)
                        : provided,
                    state
                );
        }
    }

    return merged;
}

function getCustomStyles<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
>(): StylesConfig<Option, IsMulti, Group> {
    return {
        control: (base, state) => ({
            ...base,
            backgroundColor: 'var(--ui-surface)',
            color: 'var(--ui-text)',
            borderColor: state.isFocused ? 'var(--ui-focus-ring)' : 'var(--ui-border)',
            boxShadow: state.isFocused ? '0 0 0 1px var(--ui-focus-ring)' : 'none',
            '&:hover': {borderColor: 'var(--ui-border-strong)'},
        }),
        menu: base => ({...base, backgroundColor: 'var(--ui-surface)', color: 'var(--ui-text)'}),
        menuPortal: base => ({...base, zIndex: 1060}),
        singleValue: base => ({...base, color: 'var(--ui-text)'}),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? 'var(--ui-accent-bg)'
                : state.isFocused ? 'var(--ui-surface-hover)' : 'transparent',
            color: state.isDisabled ? 'var(--ui-text-muted)' : state.isSelected ? 'var(--ui-accent)' : 'var(--ui-text)',
            '&:active': {backgroundColor: 'var(--ui-surface-active)'},
        }),
        input: base => ({...base, color: 'var(--ui-text)'}),
        placeholder: base => ({...base, color: 'var(--ui-text-muted)'}),
        multiValue: base => ({...base, backgroundColor: 'var(--ui-surface-active)'}),
        multiValueLabel: base => ({...base, color: 'var(--ui-text)'}),
        multiValueRemove: base => ({...base, color: 'var(--ui-text-secondary)', '&:hover': {backgroundColor: 'var(--ui-danger-bg)', color: 'var(--ui-danger)'}}),
        indicatorSeparator: base => ({...base, backgroundColor: 'var(--ui-border)'}),
        dropdownIndicator: base => ({...base, color: 'var(--ui-text-muted)', '&:hover': {color: 'var(--ui-text)'}}),
        clearIndicator: base => ({...base, color: 'var(--ui-text-muted)', '&:hover': {color: 'var(--ui-text)'}}),
    };
}

export const ThemeAwareSelect = <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
>(
    props: SelectProps<Option, IsMulti, Group>
) => {
    const themeStyles = getCustomStyles<Option, IsMulti, Group>();
    const mergedStyles = mergeStyles(themeStyles, props.styles);
    const menuPortalTarget =
        'menuPortalTarget' in props
            ? props.menuPortalTarget
            : getDefaultMenuPortalTarget();

    return (
        <Select
            {...props}
            styles={mergedStyles}
            menuPlacement={props.menuPlacement ?? 'auto'}
            menuPortalTarget={menuPortalTarget}
            menuPosition={props.menuPosition ?? 'fixed'}
            classNamePrefix="theme-aware-select"
        />
    );
};
